import type { BatchSaveCategoryNodePayload, CategoryNode } from '@/api/address-book/types';
import { CategorySelectType } from '@/api/address-book/types';
import { getAllIds } from '@/features/address-book/category-utils';

export const toCategoryPayload = (node: CategoryNode): BatchSaveCategoryNodePayload => ({
  id: node.id,
  name: node.name,
  selectType: node.selectType,
  children: node.children.map(toCategoryPayload),
});

export {
  CATEGORY_VARIANT_COLORS,
  CATEGORY_VARIANT_DOT_COLORS,
  CATEGORY_VARIANT_SUBTLE_COLORS,
  CATEGORY_VARIANT_TEXT_COLORS,
  getAllIds,
  getCategoryVariant,
  hasOpenCategory,
  selectsOpenCategory,
} from '@/features/address-book/category-utils';

const CATEGORY_BADGE_LABEL_LIMIT = 16;

export const getCategoryBadgeLabel = (nodeName: string): string =>
  nodeName.length > CATEGORY_BADGE_LABEL_LIMIT
    ? `${nodeName.slice(0, CATEGORY_BADGE_LABEL_LIMIT - 2)}...`
    : nodeName;

export const buildTooltipPath = (path: Array<{ name: string }>): string =>
  path.map((n) => n.name).join(' > ');

export const flattenCategoryChildren = (
  nodes: CategoryNode[],
  depth: number = 0,
  parent: CategoryNode | null = null,
): Array<{ node: CategoryNode; depth: number; parent: CategoryNode | null }> =>
  nodes.flatMap((node) => [
    { node, depth, parent },
    ...flattenCategoryChildren(node.children, depth + 1, node),
  ]);

export const getNodePath = (nodes: CategoryNode[], id: string): CategoryNode[] | null => {
  for (const node of nodes) {
    if (node.id === id) return [node];
    const childPath = getNodePath(node.children, id);
    if (childPath) return [node, ...childPath];
  }
  return null;
};

export const toggleCategoryNode = (prev: string[], roots: CategoryNode[], id: string): string[] => {
  const path = getNodePath(roots, id);
  if (!path) return prev;

  const selected = new Set(prev);
  const node = path[path.length - 1];

  if (node.locked && !selected.has(id)) return prev;

  if (selected.has(id)) {
    getAllIds([node]).forEach((descId) => selected.delete(descId));
    for (let i = path.length - 2; i >= 1; i--) {
      const ancestor = path[i];
      const hasSelectedDescendant = getAllIds(ancestor.children).some((descId) =>
        selected.has(descId),
      );
      if (hasSelectedDescendant) break;
      selected.delete(ancestor.id);
    }
    return [...selected];
  }

  path.forEach((current, index) => {
    const parent = index === 0 ? null : path[index - 1];
    if (parent?.selectType === CategorySelectType.Single) {
      parent.children.forEach((sibling) => {
        if (sibling.id !== current.id) {
          getAllIds([sibling]).forEach((descId) => selected.delete(descId));
        }
      });
    }
    // The server refuses every write that names a locked category, so a locked
    // ancestor of an open pick is never filed alongside it.
    if (index > 0 && !current.locked) selected.add(current.id);
  });

  return [...selected];
};

export const findRootName = (roots: CategoryNode[], targetId: string): string => {
  for (const root of roots) {
    if (getAllIds([root]).includes(targetId)) return root.name;
  }
  return '';
};

export const findNodeById = (nodes: CategoryNode[], id: string): CategoryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNodeById(node.children, id);
    if (found) return found;
  }
  return null;
};
